import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import layout from '../layout/layout';
import { useAppDispatch, useAppSelector } from '../Store/hooks';
import { getDashboard } from '../Store/StudentSlices/getDashboardSlice';

const formatCount = (count: number, singular: string, plural?: string) =>
  `${count} ${count === 1 ? singular : plural ?? `${singular}s`}`;

const formatDuration = (value: number) => `${value} mins`;

const getScheduleStatusStyle = (value?: string) => {
  const normalized = (value ?? '').trim().toLowerCase();
  if (normalized === 'active') {
    return { label: 'Active', className: 'bg-emerald-100 text-emerald-700' };
  }
  if (normalized === 'completed') {
    return { label: 'Completed', className: 'bg-slate-200 text-slate-700' };
  }
  return { label: 'Scheduled', className: 'bg-blue-100 text-blue-700' };
};

const quickActions = [
  {
    title: 'Connect your Calendar',
    description: 'Sync up Schedule with your Calendar',
    to: '/settings?tab=integrations',
    icon: (
      <svg
        className="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
      </svg>
    ),
  },

  {
    title: 'Update your Bio',
    description: 'Give an introduction about yourself',
    to: '/settings?tab=profile',
    icon: (
      <svg
        className="h-5 w-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c1.8-3.4 5-5 8-5s6.2 1.6 8 5" />
      </svg>
    ),
  },
];

const Dashboard = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, data } = useAppSelector((state) => state.getDashboard);

  const currentDate = useMemo(() => {
    const date = new Date();
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }, []);

  useEffect(() => {
    dispatch(getDashboard());
  }, [dispatch]);

  const stats = useMemo(() => {
    const statsData = data?.stats;
    const sessionPool = statsData?.session_pool ?? 0;
    const sessionPoolWeek = statsData?.session_pool_week ?? 0;
    const currentStudents = statsData?.current_students ?? 0;
    const activeStudents = statsData?.students_active ?? 0;
    const minutesCompleted = statsData?.minutes_completed ?? 0;
    const taughtStudents = statsData?.taught_students ?? 0;
    const targetCompleted = statsData?.target_completed ?? 0;
    const sessionsRemaining = statsData?.sessions_remaining ?? 0;

    return [
      {
        label: 'Session Pool',
        value: `${sessionPool}`,
        subtext: `${formatCount(sessionPoolWeek, 'session')} scheduled this week`,
        accent: 'text-emerald-600',
        iconBg: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
        icon: (
         <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <g clip-path="url(#clip0_325_19443)">
    <path d="M14.1665 5C13.9455 5 13.7335 5.0878 13.5772 5.24408C13.421 5.40036 13.3332 5.61232 13.3332 5.83333C13.3332 6.05435 13.421 6.26631 13.5772 6.42259C13.7335 6.57887 13.9455 6.66667 14.1665 6.66667H15.4882L11.6665 10.4883L8.50566 7.3275C8.34938 7.17127 8.13746 7.08351 7.91649 7.08351C7.69552 7.08351 7.4836 7.17127 7.32732 7.3275L1.91066 12.7442C1.83106 12.821 1.76758 12.913 1.72391 13.0147C1.68023 13.1163 1.65724 13.2257 1.65628 13.3363C1.65532 13.447 1.6764 13.5567 1.71831 13.6591C1.76021 13.7615 1.82208 13.8546 1.90033 13.9328C1.97857 14.0111 2.07161 14.073 2.17403 14.1149C2.27644 14.1568 2.38618 14.1778 2.49682 14.1769C2.60747 14.1759 2.71682 14.1529 2.81849 14.1093C2.92016 14.0656 3.01212 14.0021 3.08899 13.9225L7.91649 9.095L11.0773 12.2558C11.2336 12.4121 11.4455 12.4998 11.6665 12.4998C11.8875 12.4998 12.0994 12.4121 12.2557 12.2558L16.6665 7.845V9.16667C16.6665 9.38768 16.7543 9.59964 16.9106 9.75592C17.0668 9.9122 17.2788 10 17.4998 10C17.7208 10 17.9328 9.9122 18.0891 9.75592C18.2454 9.59964 18.3332 9.38768 18.3332 9.16667V5.83333C18.3332 5.61232 18.2454 5.40036 18.0891 5.24408C17.9328 5.0878 17.7208 5 17.4998 5H14.1665Z" fill="#12B76A"/>
  </g>
  <defs>
    <clipPath id="clip0_325_19443">
      <rect width="20" height="20" fill="white"/>
    </clipPath>
  </defs>
</svg>
        ),
      },
      {
        label: 'Current Students',
        value: `${currentStudents}`,
        subtext: `${formatCount(activeStudents, 'student')} active`,
        accent: 'text-blue-600',
        iconBg: 'bg-blue-50',
        iconColor: 'text-blue-600',
        icon: (
         <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <g clip-path="url(#clip0_325_19425)">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M4.16699 6.25C4.16699 5.75754 4.26399 5.26991 4.45244 4.81494C4.6409 4.35997 4.91712 3.94657 5.26534 3.59835C5.61356 3.25013 6.02696 2.97391 6.48193 2.78545C6.9369 2.597 7.42454 2.5 7.91699 2.5C8.40945 2.5 8.89708 2.597 9.35206 2.78545C9.80703 2.97391 10.2204 3.25013 10.5686 3.59835C10.9169 3.94657 11.1931 4.35997 11.3815 4.81494C11.57 5.26991 11.667 5.75754 11.667 6.25C11.667 7.24456 11.2719 8.19839 10.5686 8.90165C9.86538 9.60491 8.91155 10 7.91699 10C6.92243 10 5.9686 9.60491 5.26534 8.90165C4.56208 8.19839 4.16699 7.24456 4.16699 6.25ZM7.91699 4.16667C7.36446 4.16667 6.83455 4.38616 6.44385 4.77686C6.05315 5.16756 5.83366 5.69747 5.83366 6.25C5.83366 6.80253 6.05315 7.33244 6.44385 7.72314C6.83455 8.11384 7.36446 8.33333 7.91699 8.33333C8.46953 8.33333 8.99943 8.11384 9.39013 7.72314C9.78083 7.33244 10.0003 6.80253 10.0003 6.25C10.0003 5.69747 9.78083 5.16756 9.39013 4.77686C8.99943 4.38616 8.46953 4.16667 7.91699 4.16667ZM7.91699 10.8333C9.57783 10.8333 11.0878 11.34 12.1978 12.055C12.7537 12.4133 13.2295 12.8358 13.5753 13.2967C13.9145 13.7508 14.167 14.2967 14.167 14.8808C14.167 15.51 13.8753 16.0142 13.447 16.3783C13.0428 16.72 12.5162 16.9417 11.972 17.0942C10.8778 17.4 9.43033 17.5 7.91699 17.5C6.40366 17.5 4.95616 17.4 3.86283 17.0942C3.31783 16.9417 2.79116 16.72 2.38783 16.3783C1.95783 16.015 1.66699 15.51 1.66699 14.8808C1.66699 14.2958 1.91949 13.7508 2.25866 13.2975C2.60449 12.8358 3.08033 12.4125 3.63616 12.0558C4.74616 11.3392 6.25616 10.8333 7.91699 10.8333ZM7.91699 12.5C6.58616 12.5 5.38783 12.91 4.53783 13.4567C4.11283 13.73 3.79616 14.025 3.59283 14.2958C3.38449 14.5742 3.33366 14.7758 3.33366 14.8808C3.33366 14.9425 3.34616 15.0058 3.46449 15.1058C3.60866 15.2292 3.87616 15.3675 4.31116 15.4892C5.17783 15.7308 6.43866 15.8333 7.91699 15.8333C9.39533 15.8333 10.6562 15.7308 11.5228 15.4892C11.9578 15.3675 12.2253 15.2292 12.3695 15.1058C12.4878 15.0058 12.5003 14.9425 12.5003 14.8808C12.5003 14.7758 12.4495 14.5742 12.2412 14.2958C12.0378 14.025 11.7212 13.73 11.2962 13.4567C10.4462 12.91 9.24783 12.5 7.91699 12.5ZM15.0003 10.8333C16.1003 10.8333 17.0987 11.1967 17.8337 11.7092C18.5187 12.1867 19.167 12.94 19.167 13.8092C19.167 14.2983 18.9562 14.7033 18.6353 14.9975C18.3378 15.2708 17.9612 15.4375 17.5987 15.5475C16.8737 15.7667 15.9403 15.8333 15.0003 15.8333H14.8312C14.9395 15.5433 15.0003 15.225 15.0003 14.8808C15.0003 14.6942 14.982 14.5142 14.9503 14.3392L14.9112 14.1667H15.0003C15.9012 14.1667 16.6337 14.0983 17.1162 13.9525C17.3345 13.8858 17.447 13.8192 17.4953 13.7808C17.4712 13.6825 17.3412 13.3975 16.8803 13.0767C16.4785 12.8036 16.022 12.6214 15.5426 12.5428C15.0632 12.4642 14.5724 12.4911 14.1045 12.6217C13.7011 12.1426 13.228 11.7269 12.7012 11.3883C13.4129 11.0241 14.2008 10.8338 15.0003 10.8333ZM12.5003 7.5C12.5003 6.83696 12.7637 6.20107 13.2326 5.73223C13.7014 5.26339 14.3373 5 15.0003 5C15.6634 5 16.2993 5.26339 16.7681 5.73223C17.2369 6.20107 17.5003 6.83696 17.5003 7.5C17.5003 8.16304 17.2369 8.79893 16.7681 9.26777C16.2993 9.73661 15.6634 10 15.0003 10C14.3373 10 13.7014 9.73661 13.2326 9.26777C12.7637 8.79893 12.5003 8.16304 12.5003 7.5ZM15.0003 6.66667C14.7793 6.66667 14.5673 6.75446 14.4111 6.91074C14.2548 7.06702 14.167 7.27899 14.167 7.5C14.167 7.72101 14.2548 7.93297 14.4111 8.08926C14.5673 8.24554 14.7793 8.33333 15.0003 8.33333C15.2213 8.33333 15.4333 8.24554 15.5896 8.08926C15.7459 7.93297 15.8337 7.72101 15.8337 7.5C15.8337 7.27899 15.7459 7.06702 15.5896 6.91074C15.4333 6.75446 15.2213 6.66667 15.0003 6.66667Z" fill="#0034BB"/>
  </g>
  <defs>
    <clipPath id="clip0_325_19425">
      <rect width="20" height="20" fill="white"/>
    </clipPath>
  </defs>
</svg>
        ),
      },
      {
        label: 'Minutes Completed',
        value: `${minutesCompleted}`,
        subtext: `Teaching ${formatCount(taughtStudents, 'student')}`,
        accent: 'text-amber-600',
        iconBg: 'bg-amber-50',
        iconColor: 'text-amber-600',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <g clip-path="url(#clip0_325_19951)">
    <path d="M10.0003 1.66602C14.6028 1.66602 18.3337 5.39685 18.3337 9.99935C18.3337 14.6018 14.6028 18.3327 10.0003 18.3327C5.39783 18.3327 1.66699 14.6018 1.66699 9.99935C1.66699 5.39685 5.39783 1.66602 10.0003 1.66602ZM10.0003 3.33268C8.23221 3.33268 6.53652 4.03506 5.28628 5.2853C4.03604 6.53555 3.33366 8.23124 3.33366 9.99935C3.33366 11.7675 4.03604 13.4632 5.28628 14.7134C6.53652 15.9636 8.23221 16.666 10.0003 16.666C11.7684 16.666 13.4641 15.9636 14.7144 14.7134C15.9646 13.4632 16.667 11.7675 16.667 9.99935C16.667 8.23124 15.9646 6.53555 14.7144 5.2853C13.4641 4.03506 11.7684 3.33268 10.0003 3.33268ZM10.0003 4.99935C10.2044 4.99938 10.4014 5.07431 10.554 5.20994C10.7065 5.34558 10.8039 5.53247 10.8278 5.73518L10.8337 5.83268V9.65435L13.0895 11.9102C13.2389 12.0601 13.3257 12.2614 13.3322 12.473C13.3386 12.6846 13.2643 12.8908 13.1243 13.0496C12.9843 13.2084 12.789 13.3079 12.5783 13.328C12.3675 13.3481 12.157 13.2872 11.9895 13.1577L11.9112 13.0885L9.41116 10.5885C9.28164 10.4589 9.19846 10.2902 9.17449 10.1085L9.16699 9.99935V5.83268C9.16699 5.61167 9.25479 5.39971 9.41107 5.24343C9.56735 5.08715 9.77931 4.99935 10.0003 4.99935Z" fill="#F79009"/>
  </g>
  <defs>
    <clipPath id="clip0_325_19951">
      <rect width="20" height="20" fill="white"/>
    </clipPath>
  </defs>
</svg>
        ),
      },
      {
        label: 'Target Completed',
        value: `${targetCompleted}%`,
        subtext: `${formatCount(sessionsRemaining, 'session')} remaining`,
        accent: 'text-violet-600',
        iconBg: 'bg-violet-50',
        iconColor: 'text-violet-600',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <g clip-path="url(#clip0_325_19970)">
    <path d="M10.0003 1.66602C10.3128 1.66602 10.6212 1.68268 10.9253 1.71685C11.0341 1.72889 11.1394 1.76223 11.2353 1.81498C11.3312 1.86773 11.4158 1.93885 11.4842 2.02428C11.5526 2.10971 11.6035 2.20778 11.634 2.31288C11.6645 2.41799 11.674 2.52807 11.662 2.63685C11.65 2.74563 11.6166 2.85097 11.5639 2.94686C11.5111 3.04275 11.44 3.12731 11.3546 3.19572C11.2691 3.26413 11.1711 3.31504 11.066 3.34554C10.9609 3.37605 10.8508 3.38555 10.742 3.37352C9.36869 3.21989 7.98159 3.4968 6.77255 4.16595C5.56351 4.8351 4.59229 5.86343 3.99321 7.10868C3.39414 8.35393 3.19683 9.75457 3.42857 11.1169C3.66031 12.4792 4.30965 13.7358 5.28677 14.7129C6.2639 15.69 7.52051 16.3394 8.88281 16.5711C10.2451 16.8028 11.6457 16.6055 12.891 16.0065C14.1362 15.4074 15.1646 14.4362 15.8337 13.2271C16.5029 12.0181 16.7798 10.631 16.6262 9.25768C16.6141 9.1489 16.6236 9.03882 16.6541 8.93371C16.6846 8.82861 16.7355 8.73054 16.804 8.64511C16.9421 8.47258 17.1431 8.36199 17.3628 8.33768C17.5825 8.31337 17.8029 8.37732 17.9754 8.51548C18.0608 8.58388 18.1319 8.66845 18.1847 8.76434C18.2374 8.86023 18.2708 8.96557 18.2828 9.07435C18.3162 9.37851 18.3337 9.68685 18.3337 9.99935C18.3337 14.6018 14.6028 18.3327 10.0003 18.3327C5.39783 18.3327 1.66699 14.6018 1.66699 9.99935C1.66699 5.39685 5.39783 1.66602 10.0003 1.66602ZM9.97366 6.56351C10.0286 6.77745 9.99632 7.00444 9.88397 7.1946C9.77162 7.38477 9.58837 7.52255 9.37449 7.57768C8.78688 7.73161 8.27535 8.09407 7.93536 8.59744C7.59536 9.1008 7.45012 9.71068 7.52674 10.3133C7.60337 10.9158 7.89662 11.47 8.35177 11.8722C8.80693 12.2745 9.39289 12.4974 10.0003 12.4993C10.5547 12.4993 11.0933 12.3151 11.5316 11.9757C11.9699 11.6364 12.2831 11.161 12.422 10.6243C12.4811 10.4146 12.6199 10.2364 12.8088 10.1278C12.9977 10.0192 13.2216 9.98895 13.4325 10.0434C13.6434 10.0979 13.8247 10.2328 13.9373 10.4193C14.05 10.6058 14.0852 10.8289 14.0353 11.041C13.7797 12.021 13.176 12.8743 12.3371 13.4417C11.4982 14.009 10.4815 14.2515 9.47679 14.1239C8.47212 13.9963 7.54827 13.5073 6.87783 12.7483C6.20739 11.9892 5.83622 11.0121 5.83366 9.99935C5.8338 9.07558 6.1408 8.17803 6.70644 7.44768C7.27207 6.71733 8.06429 6.19557 8.95866 5.96435C9.06465 5.93703 9.17498 5.93086 9.28335 5.94619C9.39172 5.96152 9.49602 5.99804 9.59027 6.05368C9.68453 6.10932 9.7669 6.18298 9.83268 6.27046C9.89846 6.35794 9.94637 6.45752 9.97366 6.56351ZM15.4203 1.77185C15.5724 1.83491 15.7024 1.94163 15.794 2.07852C15.8855 2.21542 15.9344 2.37635 15.9345 2.54102V4.06602H17.4587C17.6234 4.06605 17.7845 4.11494 17.9215 4.20651C18.0585 4.29808 18.1653 4.42822 18.2284 4.58047C18.2914 4.73272 18.3079 4.90025 18.2758 5.06187C18.2437 5.2235 18.1643 5.37197 18.0478 5.48851L15.1003 8.43268C14.9441 8.58897 14.7322 8.6768 14.5112 8.67685H12.5003L10.977 10.201C10.8206 10.3574 10.6085 10.4452 10.3874 10.4452C10.1663 10.4452 9.95419 10.3574 9.79782 10.201C9.64146 10.0446 9.55361 9.83257 9.55361 9.61143C9.55361 9.39029 9.64146 9.17821 9.79782 9.02185L11.322 7.49935V5.48768C11.322 5.26669 11.4099 5.05476 11.5662 4.89852L14.512 1.95185C14.6285 1.83524 14.777 1.75581 14.9387 1.72363C15.1004 1.69144 15.268 1.70793 15.4203 1.77102M14.2678 4.55185L12.9887 5.83268V7.01101H14.167L15.447 5.73185H15.1012C14.8801 5.73185 14.6682 5.64405 14.5119 5.48777C14.3556 5.33149 14.2678 5.11953 14.2678 4.89852V4.55185Z" fill="#5F33C8"/>
  </g>
  <defs>
    <clipPath id="clip0_325_19970">
      <rect width="20" height="20" fill="white"/>
    </clipPath>
  </defs>
</svg>
        ),
      },
    ];
  }, [data?.stats]);

  const scheduleItems = useMemo(() => {
    const items = data?.schedule ?? [];
    return items.map((item) => {
      const statusStyle = getScheduleStatusStyle(item.status);
      return {
        time: item.time,
        duration: formatDuration(item.duration ?? 0),
        name: item.student_name || 'Student',
        focus: item.plan_title || 'Coaching',
        status: statusStyle.label,
        statusStyle: statusStyle.className,
        joinLink: item.join_link,
        avatar: item.avatar,
      };
    });
  }, [data?.schedule]);

  return (
    <div className="flex flex-col gap-6">
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {loading ? (
          <>
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="flex flex-col gap-4 rounded-[16px] border border-slate-100 bg-white px-5 py-4 shadow-sm"
              >
                <div className="h-10 w-10 animate-pulse rounded-xl bg-slate-200" />
                <div className="flex flex-col gap-1">
                  <div className="h-4 w-24 animate-pulse rounded bg-slate-200" />
                  <div className="h-8 w-16 animate-pulse rounded bg-slate-200" />
                  <div className="h-4 w-32 animate-pulse rounded bg-slate-200" />
                </div>
              </div>
            ))}
          </>
        ) : (
          stats.map((stat) => (
            <div
              key={stat.label}
              className="flex flex-col gap-4 rounded-[16px] border border-slate-100 bg-white px-5 py-4 shadow-sm"
            >
              <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${stat.iconBg} ${stat.iconColor}`}>
                {stat.icon}
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold text-slate-400">{stat.label}</p>
                <p className="text-2xl font-semibold text-slate-900">{stat.value}</p>
                <p className={`text-sm font-semibold ${stat.accent}`}>{stat.subtext}</p>
              </div>
            </div>
          ))
        )}
      </section>

      <section className="grid gap-4 xl:grid-cols-[minmax(0,_2fr)_minmax(0,_1fr)]">
        <div className="flex flex-col gap-4 rounded-[16px] border border-slate-100 bg-white px-5 py-5 shadow-sm h-[430px]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900">Today's Schedule</p>
            </div>
            <p className="text-xs font-semibold text-slate-400">{currentDate}</p>
          </div>
          <div className="flex flex-col gap-3 overflow-y-auto">
            {loading ? (
              <>
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-100 bg-white px-4 py-4 shadow-sm"
                  >
                    <div className="min-w-[160px]">
                      <div className="mb-2 h-4 w-20 animate-pulse rounded bg-slate-200" />
                      <div className="h-3 w-16 animate-pulse rounded bg-slate-200" />
                    </div>
                    <div className="flex min-w-[200px] items-center gap-3">
                      <div className="h-9 w-9 animate-pulse rounded-full bg-slate-200" />
                      <div>
                        <div className="mb-2 h-4 w-24 animate-pulse rounded bg-slate-200" />
                        <div className="h-3 w-32 animate-pulse rounded bg-slate-200" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-6 w-20 animate-pulse rounded-full bg-slate-200" />
                      <div className="h-8 w-16 animate-pulse rounded-lg bg-slate-200" />
                    </div>
                  </div>
                ))}
              </>
            ) : scheduleItems.length ? (
              scheduleItems.map((item) => (
              <div
                key={`${item.time}-${item.name}`}
                className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-100 bg-white px-4 py-4 shadow-sm"
              >
                <div className="min-w-[160px]">
                  <p className="text-sm font-semibold text-slate-900">{item.time}</p>
                  <p className="text-xs text-slate-400">{item.duration}</p>
                </div>
                <div className="flex min-w-[200px] items-center gap-3">
                  {item.avatar ? (
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="h-9 w-9 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                      {item.name
                        .split(' ')
                        .map((part) => part[0])
                        .slice(0, 2)
                        .join('')}
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{item.name}</p>
                    <p className="text-xs text-slate-400">{item.focus}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${item.statusStyle}`}>
                    {item.status}
                  </span>
                  <button
                    type="button"
                    className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={!item.joinLink}
                    onClick={() => {
                      if (item.joinLink) {
                        window.open(item.joinLink, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M23 7l-7 5 7 5V7z" />
                      <rect x="1" y="5" width="15" height="14" rx="2" />
                    </svg>
                    Join
                  </button>
                </div>
              </div>
            ))
            ) : (
              <div className="rounded-xl  text-center pt-[40px]">
                No sessions scheduled yet.
              </div>
            )}
          </div>
        </div>

        <aside className="flex flex-col gap-4 rounded-[16px] border border-slate-100 bg-white px-5 py-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-900">Quick Actions</p>
          </div>
          <div className="flex flex-col gap-3">
            {quickActions.map((action) => (
              <button
                key={action.title}
                type="button"
                className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3 text-left transition hover:bg-slate-100"
                onClick={() => navigate(action.to)}
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-slate-700 shadow-sm">
                  {action.icon}
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-slate-900">
                    {action.title}
                  </span>
                  <span className="text-xs text-slate-400">
                    {action.description}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </aside>
      </section>
    </div>
  );
};

export default layout(Dashboard);
